"use client"

import { useAdStore } from "@/lib/ad-store"
import { getMaxSources } from "@/lib/source-limits"
import { ChevronDown, Lock, AlertTriangle, ArrowUp, ArrowDown, Folder, Layers, Plus, Wand2, Image, Video, RefreshCw, Eye, EyeOff, Key, KeyRound, Database, Cloud, Shield, CheckCircle, Flag } from "@/lib/icons"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { useSourceStore } from "@/lib/source-store"
import { useBooruCredentialsStore } from "@/lib/booru-credentials-store"
import { useSettingsStore } from "@/lib/settings-store"
import { BooruAuthDialog } from "@/components/booru-auth-dialog"
import { CloudflareVerifier } from "@/components/cloudflare-verifier"
import { ReportSourceDialog } from "@/components/report-source-dialog"
import { useCloudflareVerificationStore } from "@/lib/cloudflare-verification-store"
import type { BooruContentTypeVisibility, BooruMediaType, BooruVisibilityType, BooruPost, BooruSite } from "@/lib/booru-api"
import type { SourceConfig } from "@/lib/source-store"
import type { FilterRating } from "@/lib/rating-filter"
import { useTranslations } from "next-intl"
import type { PostDateRange } from "@/lib/post-date"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { useTrendingTags } from "@/hooks/use-trending-tags"
import { TrendingTagBadges } from "@/components/trending-tag-badges"

type FilterPanelSectionsOpen = {
  sortBy: boolean
  dateRange: boolean
  sources: boolean
  rating: boolean
  contentFilters: boolean
  trendingTags: boolean
}

const FILTER_PANEL_SECTIONS_OPEN_STORAGE_KEY = "goonru:filter-panel:sections-open:v1"

interface FilterPanelProps {
  selectedSites: BooruSite[]
  onSitesChange: (sites: BooruSite[]) => void
  activeRatings: FilterRating[]
  onActiveRatingsChange: (ratings: FilterRating[]) => void
  sortBy: "score" | "date" | "random" | "rating" | "downloads" | "favorites" | "relevance" | "duration"
  onSortByChange: (sortBy: "score" | "date" | "random" | "rating" | "downloads" | "favorites" | "relevance" | "duration") => void
  onRandomizeSorting?: () => void
  sortDirection?: "asc" | "desc"
  onSortDirectionChange?: (direction: "asc" | "desc") => void
  dateRange?: PostDateRange
  onDateRangeChange?: (range: PostDateRange | undefined) => void
  viewMode?: "browse" | "collections" | "editors" | "gooncave" | "game-arena"
  onViewModeChange?: (mode: "browse" | "collections" | "editors" | "gooncave" | "game-arena") => void
  filterAIImages?: boolean
  onFilterAIImagesChange?: (filter: boolean) => void
  contentTypeVisibility?: BooruContentTypeVisibility
  onContentTypeVisibilityChange?: (visibility: BooruContentTypeVisibility) => void
  gifsOnly?: boolean
  onGifsOnlyChange?: (value: boolean) => void
  currentPosts?: BooruPost[]
  onTagAdd?: (tag: string) => void
}

// Each rating has its own color so it reads at a glance: safe green, questionable blue, explicit purple, and the
// extreme tier (below) red. Fixed hex values on purpose: the app theme remaps Tailwind's purple/violet (and the blue
// that sits next to it) to its brand color, which made Questionable and Explicit look the same.
const RATING_OPTIONS: { value: FilterRating; labelKey: "ratingSafe" | "ratingQuestionable" | "ratingExplicit"; color: string; checked: string; box: string }[] = [
  { value: "safe", labelKey: "ratingSafe", color: "text-[#16a34a] dark:text-[#4ade80]", checked: "border-[#16a34a]/50 bg-[#16a34a]/10", box: "border-[#16a34a]/60 data-[state=checked]:bg-[#16a34a] data-[state=checked]:border-[#16a34a] dark:data-[state=checked]:bg-[#16a34a] data-[state=checked]:text-white" },
  { value: "questionable", labelKey: "ratingQuestionable", color: "text-[#2563eb] dark:text-[#60a5fa]", checked: "border-[#2563eb]/50 bg-[#2563eb]/10", box: "border-[#2563eb]/60 data-[state=checked]:bg-[#2563eb] data-[state=checked]:border-[#2563eb] dark:data-[state=checked]:bg-[#2563eb] data-[state=checked]:text-white" },
  { value: "explicit", labelKey: "ratingExplicit", color: "text-[#a21caf] dark:text-[#e879f9]", checked: "border-[#a21caf]/50 bg-[#a21caf]/10", box: "border-[#a21caf]/60 data-[state=checked]:bg-[#a21caf] data-[state=checked]:border-[#a21caf] dark:data-[state=checked]:bg-[#a21caf] data-[state=checked]:text-white" },
]

export function FilterPanel({
  selectedSites,
  onSitesChange,
  activeRatings,
  onActiveRatingsChange,
  sortBy,
  onSortByChange,
  onRandomizeSorting,
  sortDirection = "desc",
  onSortDirectionChange,
  dateRange,
  onDateRangeChange,
  viewMode = "browse",
  onViewModeChange,
  filterAIImages = false,
  onFilterAIImagesChange,
  contentTypeVisibility = { album: true, image: true, video: true },
  onContentTypeVisibilityChange,
  gifsOnly = false,
  onGifsOnlyChange,
  onTagAdd,
}: FilterPanelProps) {
  const tt = useTranslations("filterPanel")
  const tTrending = useTranslations("trendingTags")
  // Which source the "report a problem" dialog is open for (null = closed).
  const [reportingSource, setReportingSource] = useState<{ id: string; name: string } | null>(null)
  const [datePreset, setDatePreset] = useState<"all" | "day" | "week" | "month" | "year" | "custom">("all")
  // Custom range: the two day pickers ("YYYY-MM-DD", either may be empty = open-ended), and whether posts with no date stay in.
  const [customFrom, setCustomFrom] = useState("")
  const [customTo, setCustomTo] = useState("")
  const includeUndated = dateRange?.includeUndated === true
  const [sectionsOpenLoaded, setSectionsOpenLoaded] = useState(false)
  const [sectionsOpen, setSectionsOpen] = useState<FilterPanelSectionsOpen>({
    sortBy: false,
    dateRange: false,
    sources: true,
    rating: false,
    contentFilters: false,
    trendingTags: false,
  })
  // Trending characters, series and artists on the selected sources (the same list the search bar offers on an empty click).
  const trending = useTrendingTags(selectedSites, { enabled: sectionsOpen.trendingTags, limit: 30 })
  const [authDialogOpen, setAuthDialogOpen] = useState(false)
  const [authDialogSource, setAuthDialogSource] = useState<SourceConfig | null>(null)
  const router = useRouter()

  const { sources, toggleSource } = useSourceStore()
  const { getCredentials } = useBooruCredentialsStore()
  const { sortDataSource, setSortDataSource } = useSettingsStore()

  const enabledSources = sources.filter((s) => s.enabled && !s.disabled)
  const allSources = sources

  // Auto-remove disabled sources from selectedSites
  useEffect(() => {
    const enabledSourceIds = enabledSources.map(s => s.id)
    const invalidSelectedSites = selectedSites.filter(siteId => !enabledSourceIds.includes(siteId))
    
    if (invalidSelectedSites.length > 0) {
      console.log(`[FilterPanel] Removing disabled sources from selection:`, invalidSelectedSites)
      const validSelectedSites = selectedSites.filter(siteId => enabledSourceIds.includes(siteId))
      onSitesChange(validSelectedSites)
    }
  }, [enabledSources, selectedSites, onSitesChange])

  useEffect(() => {
    try {
      const raw = localStorage.getItem(FILTER_PANEL_SECTIONS_OPEN_STORAGE_KEY)
      if (!raw) {
        setSectionsOpenLoaded(true)
        return
      }

      const parsed = JSON.parse(raw) as Partial<FilterPanelSectionsOpen>
      if (!parsed || typeof parsed !== "object") {
        setSectionsOpenLoaded(true)
        return
      }

      setSectionsOpen((prev) => ({
        ...prev,
        sortBy: Boolean(parsed.sortBy),
        dateRange: Boolean(parsed.dateRange),
        sources: Boolean(parsed.sources),
        rating: Boolean(parsed.rating),
        contentFilters: Boolean(parsed.contentFilters),
        trendingTags: Boolean(parsed.trendingTags),
      }))
      setSectionsOpenLoaded(true)
    } catch {
      setSectionsOpenLoaded(true)
    }
  }, [])

  useEffect(() => {
    if (!sectionsOpenLoaded) return
    try {
      localStorage.setItem(FILTER_PANEL_SECTIONS_OPEN_STORAGE_KEY, JSON.stringify(sectionsOpen))
    } catch {}
  }, [sectionsOpen, sectionsOpenLoaded])

  const setContentVisibility = (visibility: BooruContentTypeVisibility) => {
    onContentTypeVisibilityChange?.(visibility)
  }

  // BooruVisibilityType, not BooruMediaType: visibility only covers the three
  // types getPostMediaType() produces, and this is only ever called with
  // "album" | "image" | "video".
  const toggleContentVisibility = (type: BooruVisibilityType) => {
    setContentVisibility({ ...contentTypeVisibility, [type]: !contentTypeVisibility[type] })
  }

  const presetLabel = (visibility: BooruContentTypeVisibility) => {
    const a = visibility.album
    const i = visibility.image
    const v = visibility.video
    if (a && i && v) return "All"
    if (a && !i && !v) return "Albums only"
    if (!a && i && !v) return "Images only"
    if (!a && !i && v) return "Videos only"
    if (!a && i && v) return "Hide albums"
    if (a && i && !v) return "Hide videos"
    if (a && !i && v) return "Hide images"
    return "Custom"
  }

  const cycleContentPreset = () => {
    const presets: BooruContentTypeVisibility[] = [
      { album: true, image: true, video: true },
      { album: true, image: false, video: false },
      { album: false, image: true, video: false },
      { album: false, image: false, video: true },
      { album: false, image: true, video: true },
      { album: true, image: true, video: false },
      { album: true, image: false, video: true },
    ]

    const same = (a: BooruContentTypeVisibility, b: BooruContentTypeVisibility) =>
      a.album === b.album && a.image === b.image && a.video === b.video

    const currentIndex = presets.findIndex((p) => same(p, contentTypeVisibility))
    const next = presets[(currentIndex + 1) % presets.length]
    setContentVisibility(next)
  }

  const toggleSite = (siteId: string) => {
    // Only update the selectedSites prop for filtering - DO NOT call toggleSource()
    const site = siteId as BooruSite
    const MAX_SOURCES = getMaxSources(useAdStore.getState().isPremiumUser) // free 3, paid 6 — see lib/source-limits.ts

    // Read the store directly rather than trusting the `selectedSites` prop:
    // two toggles fired in quick succession (two checkboxes clicked within the
    // same React batch) both closed over the SAME stale prop snapshot, so the
    // second call computed its next array from state that didn't include the
    // first call's still-pending change. Whichever onSitesChange() landed
    // last then overwrote the store outright — Zustand's set() here isn't a
    // merge — so one of the two clicks silently didn't stick. getState() is
    // always current regardless of what this component's own props have
    // flushed to yet.
    const currentSelected = useSettingsStore.getState().selectedSites

    if (currentSelected.includes(site)) {
      // Deselecting - always allow
      onSitesChange(currentSelected.filter((s) => s !== site))
    } else {
      // Selecting - check limit
      if (currentSelected.length >= MAX_SOURCES) {
        // Show toast notification about limit
        if (typeof window !== 'undefined') {
          const toast = (window as any).toast
          if (toast) {
            toast.error(tt("sourceLimitReached"), {
              description: tt("yourCurrentMembershipTierAllowsUpToMAXSO", { mAXSOURCES: MAX_SOURCES })
            })
          }
        }
        return
      }
      onSitesChange([...currentSelected, site])
    }
  }

  const getDateRange = (preset: string) => {
    const now = new Date()
    const from = new Date()

    switch (preset) {
      case "day":
        from.setDate(from.getDate() - 1)
        break
      case "week":
        from.setDate(from.getDate() - 7)
        break
      case "month":
        from.setMonth(from.getMonth() - 1)
        break
      case "year":
        from.setFullYear(from.getFullYear() - 1)
        break
      default:
        from.setFullYear(2000)
    }

    // Open-ended on the "now" side: the range is fixed at the moment of the click, so an exact end time would
    // wrongly exclude anything posted afterwards (e.g. posts that load as you scroll).
    return { from, to: new Date(now.getTime() + 24 * 60 * 60 * 1000), ...(includeUndated && { includeUndated: true }) }
  }

  // A picked day starts at its first moment and ends at its last, in the viewer's own time zone.
  const dayStart = (value: string) => new Date(`${value}T00:00:00`)
  const dayEnd = (value: string) => new Date(`${value}T23:59:59.999`)
  const emitCustomRange = (fromValue: string, toValue: string, undated: boolean) => {
    if (!onDateRangeChange) return
    if (!fromValue && !toValue) {
      onDateRangeChange(undefined)
      return
    }
    let from = fromValue ? dayStart(fromValue) : new Date(2000, 0, 1)
    let to = toValue ? dayEnd(toValue) : new Date(Date.now() + 24 * 60 * 60 * 1000)
    if (from.getTime() > to.getTime()) [from, to] = [dayStart(toValue || fromValue), dayEnd(fromValue || toValue)] // picked backwards: swap
    onDateRangeChange({ from, to, ...(undated && { includeUndated: true }) })
  }

  const handleDatePreset = (preset: string) => {
    setDatePreset(preset as any)
    if (preset === "all") {
      onDateRangeChange?.(undefined)
    } else if (preset === "custom") {
      emitCustomRange(customFrom, customTo, includeUndated)
    } else if (onDateRangeChange) {
      onDateRangeChange(getDateRange(preset))
    }
  }

  const handleIncludeUndated = (next: boolean) => {
    if (!dateRange || !onDateRangeChange) return
    onDateRangeChange({ ...dateRange, includeUndated: next })
  }

  return (
    <div className="glass glass-denser rounded-xl p-3 space-y-3 shadow-sm">
      {/* Header (icon + "Filters" title + close button) now lives in the
          Sheet wrapper in app/page.tsx, matching Settings — this component
          used to duplicate it here with no close button of its own. */}

      {/* Advanced Sort Section */}
      <Collapsible
        open={sectionsOpen.sortBy}
        onOpenChange={(open) => setSectionsOpen((prev) => ({ ...prev, sortBy: open }))}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
          <span className="text-xs font-semibold text-foreground">{tt("sortBy")}</span>
          <ChevronDown className="h-3.5 w-3.5 transition-transform data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-1.5">
          {/* Data Source Toggle */}
          <div className="flex gap-1 mb-2">
            <Button
              variant={sortDataSource === "app" ? "default" : "ghost"}
              className={`flex-1 h-8 text-xs transition-all ${sortDataSource === "app" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
              size="sm"
              onClick={() => setSortDataSource("app")}
              title={tt("sortByYourAppDataRatingsFavoritesDownl")}
            >
              <Database className="h-3 w-3 mr-1" />
              {tt("appData")}
            </Button>
            <Button
              variant={sortDataSource === "source" ? "default" : "ghost"}
              className={`flex-1 h-8 text-xs transition-all ${sortDataSource === "source" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
              size="sm"
              onClick={() => setSortDataSource("source")}
              title={tt("sortBySourceSiteDataScoreDatePopularit")}
            >
              <Cloud className="h-3 w-3 mr-1" />
              {tt("sourceData")}
            </Button>
          </div>

          <div className="h-px bg-border/30" />

          {/* Direction Toggle */}
          <div className="flex gap-1">
            <Button
              variant={sortDirection === "desc" ? "default" : "ghost"}
              className={`flex-1 h-8 text-xs transition-all ${sortDirection === "desc" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
              size="sm"
              onClick={() => onSortDirectionChange?.("desc")}
            >
              <ArrowDown className="h-3 w-3 mr-1" />
              {tt("highToLow")}
            </Button>
            <Button
              variant={sortDirection === "asc" ? "default" : "ghost"}
              className={`flex-1 h-8 text-xs transition-all ${sortDirection === "asc" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
              size="sm"
              onClick={() => onSortDirectionChange?.("asc")}
            >
              <ArrowUp className="h-3 w-3 mr-1" />
              {tt("lowToHigh")}
            </Button>
          </div>

          <div className="h-px bg-border/30" />

          {/* App Data Sort Options */}
          {sortDataSource === "app" && (
            <>
              <Button
                variant={sortBy === "rating" ? "default" : "ghost"}
                className={`w-full justify-start h-8 text-xs transition-all ${sortBy === "rating" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
                size="sm"
                onClick={() => onSortByChange(sortBy === "rating" ? "score" : "rating")}
              >
                {tt("byYourRating")}
              </Button>
              <Button
                variant={sortBy === "downloads" ? "default" : "ghost"}
                className={`w-full justify-start h-8 text-xs transition-all ${sortBy === "downloads" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
                size="sm"
                onClick={() => onSortByChange(sortBy === "downloads" ? "score" : "downloads")}
              >
                {tt("byDownloads")}
              </Button>
              <Button
                variant={sortBy === "favorites" ? "default" : "ghost"}
                className={`w-full justify-start h-8 text-xs transition-all ${sortBy === "favorites" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
                size="sm"
                onClick={() => onSortByChange(sortBy === "favorites" ? "score" : "favorites")}
              >
                {tt("byFavorites")}
              </Button>
              <Button
                variant={sortBy === "relevance" ? "default" : "ghost"}
                className={`w-full justify-start h-8 text-xs transition-all ${sortBy === "relevance" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
                size="sm"
                onClick={() => onSortByChange(sortBy === "relevance" ? "score" : "relevance")}
                title={tt("sortByHowWellPostsMatchYourSearchTagsP")}
              >
                {tt("byRelevance")}
              </Button>
              <Button
                variant={sortBy === "duration" ? "default" : "ghost"}
                className={`w-full justify-start h-8 text-xs transition-all ${sortBy === "duration" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
                size="sm"
                onClick={() => onSortByChange(sortBy === "duration" ? "score" : "duration")}
              >
                {tt("byDuration")}
              </Button>
              <Button
                variant={sortBy === "random" ? "default" : "ghost"}
                className={`w-full justify-start h-8 text-xs transition-all ${sortBy === "random" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
                size="sm"
                onClick={() => onSortByChange(sortBy === "random" ? "score" : "random")}
              >
                {tt("randomOrder")}
              </Button>
              {sortBy === "random" && (
                <Button
                  variant="ghost"
                  className="w-full justify-start h-8 text-xs border border-border/30 bg-background/20 hover:bg-background/30"
                  size="sm"
                  onClick={onRandomizeSorting}
                >
                  <RefreshCw className="h-3 w-3 mr-2" />
                  {tt("randomizeNow")}
                </Button>
              )}
            </>
          )}

          {/* Source Data Sort Options */}
          {sortDataSource === "source" && (
            <>
              <Button
                variant={sortBy === "score" ? "default" : "ghost"}
                className={`w-full justify-start h-8 text-xs transition-all ${sortBy === "score" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
                size="sm"
                onClick={() => onSortByChange(sortBy === "score" ? "score" : "score")}
              >
                {tt("byScore")}
              </Button>
              <Button
                variant={sortBy === "date" ? "default" : "ghost"}
                className={`w-full justify-start h-8 text-xs transition-all ${sortBy === "date" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
                size="sm"
                onClick={() => onSortByChange(sortBy === "date" ? "score" : "date")}
              >
                {tt("byDate")}
              </Button>
              <Button
                variant={sortBy === "relevance" ? "default" : "ghost"}
                className={`w-full justify-start h-8 text-xs transition-all ${sortBy === "relevance" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
                size="sm"
                onClick={() => onSortByChange(sortBy === "relevance" ? "score" : "relevance")}
                title={tt("sortByHowWellPostsMatchYourSearchTagsP")}
              >
                {tt("byRelevance")}
              </Button>
              <Button
                variant={sortBy === "duration" ? "default" : "ghost"}
                className={`w-full justify-start h-8 text-xs transition-all ${sortBy === "duration" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
                size="sm"
                onClick={() => onSortByChange(sortBy === "duration" ? "score" : "duration")}
              >
                {tt("byDuration")}
              </Button>
              <Button
                variant={sortBy === "random" ? "default" : "ghost"}
                className={`w-full justify-start h-8 text-xs transition-all ${sortBy === "random" ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md" : "hover:bg-accent hover:text-accent-foreground"}`}
                size="sm"
                onClick={() => onSortByChange(sortBy === "random" ? "score" : "random")}
              >
                {tt("randomOrder")}
              </Button>
              {sortBy === "random" && (
                <Button
                  variant="ghost"
                  className="w-full justify-start h-8 text-xs border border-border/30 bg-background/20 hover:bg-background/30"
                  size="sm"
                  onClick={onRandomizeSorting}
                >
                  <RefreshCw className="h-3 w-3 mr-2" />
                  {tt("randomizeNow")}
                </Button>
              )}
            </>
          )}
        </CollapsibleContent>
      </Collapsible>

      <div className="h-px bg-border/30" />

      <Collapsible
        open={sectionsOpen.dateRange}
        onOpenChange={(open) => setSectionsOpen((prev) => ({ ...prev, dateRange: open }))}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
          <span className="text-xs font-medium text-foreground">{tt("dateRange")}</span>
          <ChevronDown className="h-3.5 w-3.5 transition-transform data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-1.5">
          <Button
            variant={datePreset === "all" ? "default" : "ghost"}
            className="w-full justify-start h-8 text-xs"
            size="sm"
            onClick={() => handleDatePreset("all")}
          >
            {tt("allTime")}
          </Button>
          <Button
            variant={datePreset === "year" ? "default" : "ghost"}
            className="w-full justify-start h-8 text-xs"
            size="sm"
            onClick={() => handleDatePreset("year")}
          >
            {tt("pastYear")}
          </Button>
          <Button
            variant={datePreset === "month" ? "default" : "ghost"}
            className="w-full justify-start h-8 text-xs"
            size="sm"
            onClick={() => handleDatePreset("month")}
          >
            {tt("pastMonth")}
          </Button>
          <Button
            variant={datePreset === "week" ? "default" : "ghost"}
            className="w-full justify-start h-8 text-xs"
            size="sm"
            onClick={() => handleDatePreset("week")}
          >
            {tt("pastWeek")}
          </Button>
          <Button
            variant={datePreset === "day" ? "default" : "ghost"}
            className="w-full justify-start h-8 text-xs"
            size="sm"
            onClick={() => handleDatePreset("day")}
          >
            {tt("past24Hours")}
          </Button>
          <Button
            variant={datePreset === "custom" ? "default" : "ghost"}
            className="w-full justify-start h-8 text-xs"
            size="sm"
            onClick={() => handleDatePreset("custom")}
          >
            {tt("customRange")}
          </Button>
          {datePreset === "custom" && (
            <div className="grid grid-cols-2 gap-2 px-1" data-testid="custom-date-range">
              <label className="flex flex-col gap-1 text-[11px] text-muted-foreground">
                {tt("rangeFrom")}
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e) => {
                    setCustomFrom(e.target.value)
                    emitCustomRange(e.target.value, customTo, includeUndated)
                  }}
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] text-muted-foreground">
                {tt("rangeTo")}
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={(e) => {
                    setCustomTo(e.target.value)
                    emitCustomRange(customFrom, e.target.value, includeUndated)
                  }}
                />
              </label>
            </div>
          )}
          {dateRange && (
            <div className="flex items-center justify-between gap-3 px-1 pt-1">
              <Label htmlFor="include-undated" className="text-[11px] font-normal text-muted-foreground">
                {tt("includeUndated")}
              </Label>
              <Switch id="include-undated" checked={includeUndated} onCheckedChange={handleIncludeUndated} />
            </div>
          )}
          {dateRange && !includeUndated && (
            <p className="px-1 text-[11px] text-muted-foreground">
              {tt("datedPostsNote")}
            </p>
          )}
        </CollapsibleContent>
      </Collapsible>

      <div className="h-px bg-border/30" />

      {/* Sites - Now reads from unified source store */}
      <Collapsible
        open={sectionsOpen.sources}
        onOpenChange={(open) => setSectionsOpen((prev) => ({ ...prev, sources: open }))}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-foreground">{tt("currentSourcesLength", { length: enabledSources.length })}</span>
            {selectedSites.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/20 text-primary font-medium">
                {tt("length3Selected", { length: selectedSites.length })}
              </span>
            )}
          </div>
          <ChevronDown className="h-3.5 w-3.5 transition-transform data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-2">
          <div className="mb-3 pb-3 border-b border-border/30">
            <Button
              variant={viewMode === "collections" ? "default" : "ghost"}
              className={`w-full justify-start h-8 text-xs shadow-sm hover:shadow-md ${viewMode === "collections" ? "" : "bg-card border border-border"}`}
              size="sm"
              onClick={() => onViewModeChange?.("collections")}
            >
              <Folder className="h-3 w-3 mr-2" />
              {tt("browseCollection")}
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-8 text-xs mt-1 bg-card border border-border shadow-sm hover:shadow-md"
              size="sm"
              onClick={() => router.push("/sources")}
            >
              <Plus className="h-3 w-3 mr-2" />
              {tt("manageSources")}
            </Button>
          </div>

          <TooltipProvider>
            <div className="max-h-[300px] overflow-y-auto space-y-1">
              {enabledSources.map((source) => {
                const hasCredentials = !!getCredentials(source.id)
                const supportsAuth = source.auth?.type === "api_key" || source.auth?.type === "optional_api_key"
                const isDisabled = (source as any).disabled === true
                
                return (
                  <div key={source.id} className={`flex items-center gap-2 ${isDisabled ? 'opacity-40 pointer-events-none' : ''}`}>
                    <Checkbox
                      id={`site-${source.id}`}
                      checked={selectedSites.includes(source.id as BooruSite)}
                      onCheckedChange={() => toggleSite(source.id)}
                      className="h-4 w-4 data-[state=unchecked]:!border-foreground/15 data-[state=unchecked]:bg-background/80 shadow-sm"
                      disabled={isDisabled}
                    />
                    <Label htmlFor={`site-${source.id}`} className={`text-xs flex items-center gap-1.5 flex-1 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                      {(source.faviconUrl || source.icon) && (
                        <Avatar className="h-4 w-4 shrink-0">
                          <AvatarImage src={source.faviconUrl} alt={source.name} referrerPolicy="no-referrer" />
                          <AvatarFallback className="text-[10px] leading-none">{source.icon || "🌐"}</AvatarFallback>
                        </Avatar>
                      )}
                      {source.name}
                      {isDisabled && <span className="text-xs text-red-500 font-semibold">(OUT OF SERVICE)</span>}
                      {source.tagLimit != null && source.tagLimit > 0 && !isDisabled && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-xs text-muted-foreground">{tt("maxTagLimit", { tagLimit: source.tagLimit })}</span>
                          </TooltipTrigger>
                          <TooltipContent side="top" collisionPadding={8} className="max-w-[170px] bg-slate-900 border border-slate-700 shadow-lg p-3 text-slate-50">
                            <p className="text-xs">
                              {tt("limitedToTagLimitTagsPerSearchExtraTag", { tagLimit: source.tagLimit })}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </Label>
                    
                    {/* Auth button - show for all sources that support auth and not disabled */}
                    {supportsAuth && !isDisabled && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-6 w-6 p-0 ${hasCredentials ? 'text-green-500 hover:text-green-400' : 'text-muted-foreground hover:text-foreground'}`}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setAuthDialogSource(source)
                              setAuthDialogOpen(true)
                            }}
                          >
                            {hasCredentials ? (
                              <KeyRound className="h-4 w-4" />
                            ) : (
                              <Key className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          collisionPadding={8}
                          className="max-w-[170px] bg-slate-900 border border-slate-700 shadow-lg p-3"
                        >
                          <div className="flex items-center gap-2">
                            {hasCredentials ? (
                              <>
                                <KeyRound className="h-3.5 w-3.5 text-green-400" />
                                <p className="text-sm font-medium text-green-50">{tt("credentialsConfigured")}</p>
                              </>
                            ) : (
                              <>
                                <Key className="h-3.5 w-3.5 text-slate-400" />
                                <p className="text-sm font-medium text-slate-50">{tt("addAPICredentials")}</p>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-slate-200 mt-1">
                            {hasCredentials 
                              ? 'Click to update your API credentials'
                              : 'Click to add your API credentials for better access'
                            }
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    
                    {/* Cloudflare Verify button - show for all sources */}
                    {!isDisabled && (() => {
                      const { isVerified } = useCloudflareVerificationStore.getState()
                      const verified = isVerified(source.id)
                      
                      return (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                            }}>
                              <CloudflareVerifier
                                sourceId={source.id}
                                variant="icon"
                                className={verified ? 'text-green-500 hover:text-green-400' : 'text-muted-foreground hover:text-foreground'}
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            collisionPadding={8}
                            className="max-w-[170px] bg-slate-900 border border-slate-700 shadow-lg p-3"
                          >
                            <div className="flex items-center gap-2">
                              {verified ? (
                                <>
                                  <CheckCircle className="h-3.5 w-3.5 text-green-400" />
                                  <p className="text-sm font-medium text-green-50">{tt("cloudflareVerified")}</p>
                                </>
                              ) : (
                                <>
                                  <Shield className="h-3.5 w-3.5 text-slate-400" />
                                  <p className="text-sm font-medium text-slate-50">{tt("verifyCloudflare")}</p>
                                </>
                              )}
                            </div>
                            <p className="text-xs text-slate-200 mt-1">
                              {verified 
                                ? 'This source is verified and can bypass Cloudflare protection'
                                : 'Click to verify and bypass Cloudflare protection if blocked'
                              }
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      )
                    })()}

                    {/* Report broken source — the automated scraper-health probe only
                        catches failures its strategy runner recognises, so a source that
                        200s with an empty page or a Cloudflare interstitial looks healthy
                        until a user says otherwise. */}
                    {!isDisabled && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Report ${source.name}`}
                            className="h-7 w-7 text-muted-foreground hover:text-amber-500"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setReportingSource({ id: source.id, name: source.name })
                            }}
                          >
                            <Flag className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          collisionPadding={8}
                          className="max-w-[170px] bg-slate-900 border border-slate-700 shadow-lg p-3"
                        >
                          <div className="flex items-center gap-2">
                            <Flag className="h-3.5 w-3.5 text-amber-400" />
                            <p className="text-sm font-medium text-slate-50">{tt("reportAProblem")}</p>
                          </div>
                          <p className="text-xs text-slate-200 mt-1">
                            {tt("notLoadingOrReturningNothingSendAReport")}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                )
              })}
            </div>
          </TooltipProvider>

          {/* Show notification if using sources without credentials */}
          {selectedSites.some(siteId => {
            const source = enabledSources.find(s => s.id === siteId)
            if (!source) return false
            const supportsAuth = source.auth?.type === "api_key" || source.auth?.type === "optional_api_key"
            const hasCredentials = !!getCredentials(source.id)
            return supportsAuth && !hasCredentials
          }) && (
            <div className="mt-3 p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-start gap-2">
                <Key className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-amber-800 dark:text-amber-400 font-medium">
                    {tt("addCredentialsForBetterResults")}
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400/80 mt-0.5">
                    {tt("clickTheKeyIconNextToSourcesForHigherRa")}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      <div className="h-px bg-border/30" />

      {/* Rating */}
      <Collapsible
        open={sectionsOpen.rating}
        onOpenChange={(open) => setSectionsOpen((prev) => ({ ...prev, rating: open }))}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
          <span className="text-xs font-medium text-foreground">{tt("rating")}</span>
          <ChevronDown className="h-3.5 w-3.5 transition-transform data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-1.5">
          <p className="px-1 text-[11px] text-muted-foreground">{tt("ratingPickHint")}</p>
          {RATING_OPTIONS.map((option) => {
            const checked = activeRatings.includes(option.value)
            // Never let the last switched-on rating be turned off: that would hide every post.
            const isLastOn = checked && activeRatings.length === 1
            return (
              <label
                key={option.value}
                className={`flex h-8 w-full cursor-pointer items-center gap-2 rounded-md border px-2 text-xs ${
                  checked ? option.checked : "border-border/30 bg-background/20"
                } ${isLastOn ? "cursor-not-allowed opacity-80" : ""}`}
              >
                <Checkbox
                  className={option.box}
                  checked={checked}
                  disabled={isLastOn}
                  onCheckedChange={(next) => {
                    const without = activeRatings.filter((rating) => rating !== option.value)
                    onActiveRatingsChange(next ? [...without, option.value] : without)
                  }}
                />
                <span className={option.color}>{tt(option.labelKey)}</span>
              </label>
            )
          })}
        </CollapsibleContent>
      </Collapsible>

      <div className="h-px bg-border/30" />

      {/* Content Filters */}
      <Collapsible
        open={sectionsOpen.contentFilters}
        onOpenChange={(open) => setSectionsOpen((prev) => ({ ...prev, contentFilters: open }))}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
          <span className="text-xs font-semibold text-foreground">{tt("contentFilters")}</span>
          <ChevronDown className="h-3.5 w-3.5 transition-transform data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-1.5">
          <Button
            variant="ghost"
            className={`w-full justify-start h-8 text-xs border ${
              filterAIImages
                ? "bg-amber-500/10 border-amber-500/30 text-amber-200 hover:bg-amber-500/15"
                : "bg-background/20 border-border/30 text-foreground hover:bg-background/30"
            }`}
            size="sm"
            onClick={() => onFilterAIImagesChange?.(!filterAIImages)}
          >
            <Wand2 className="h-3 w-3 mr-2" />
            {tt("aIContent")}
            <span className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wide">
              {filterAIImages ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              {filterAIImages ? "hide" : "show"}
            </span>
          </Button>

          <Button
            variant="ghost"
            className="w-full justify-start h-8 text-xs border border-border/30 bg-background/20 hover:bg-background/30"
            size="sm"
            onClick={cycleContentPreset}
          >
            <RefreshCw className="h-3 w-3 mr-2" />
            {tt("cycleValue", { value: presetLabel(contentTypeVisibility) })}
          </Button>

          <Button
            variant="ghost"
            className={`w-full justify-start h-8 text-xs border ${
              contentTypeVisibility.album
                ? "bg-blue-600/20 border-blue-600/40 text-blue-600 hover:bg-blue-600/25"
                : "bg-rose-500/10 border-border/30 text-muted-foreground hover:bg-secondary/40"
            }`}
            size="sm"
            onClick={() => toggleContentVisibility("album")}
          >
            <Layers className="h-3 w-3 mr-2" />
            {tt("albumsComicsDoujins")}
            <span className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wide">
              {contentTypeVisibility.album ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              {contentTypeVisibility.album ? "show" : "hide"}
            </span>
          </Button>
          <Button
            variant="ghost"
            className={`w-full justify-start h-8 text-xs border ${
              contentTypeVisibility.image
                ? "bg-blue-600/20 border-blue-600/40 text-blue-600 hover:bg-blue-600/25"
                : "bg-rose-500/10 border-border/30 text-muted-foreground hover:bg-secondary/40"
            }`}
            size="sm"
            onClick={() => toggleContentVisibility("image")}
          >
            <Image className="h-3 w-3 mr-2" />
            {tt("images")}
            <span className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wide">
              {contentTypeVisibility.image ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              {contentTypeVisibility.image ? "show" : "hide"}
            </span>
          </Button>
          <Button
            variant="ghost"
            className={`w-full justify-start h-8 text-xs border ${
              contentTypeVisibility.video
                ? "bg-blue-600/20 border-blue-600/40 text-blue-600 hover:bg-blue-600/25"
                : "bg-rose-500/10 border-border/30 text-muted-foreground hover:bg-secondary/40"
            }`}
            size="sm"
            onClick={() => toggleContentVisibility("video")}
          >
            <Video className="h-3 w-3 mr-2" />
            {tt("videosInclGIF")}
            <span className="ml-auto flex items-center gap-1 text-[10px] uppercase tracking-wide">
              {contentTypeVisibility.video ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              {contentTypeVisibility.video ? "show" : "hide"}
            </span>
          </Button>
          <Button
            variant="ghost"
            className={`w-full justify-start h-8 text-xs border ${
              gifsOnly
                ? "bg-blue-600/20 border-blue-600/40 text-blue-600 hover:bg-blue-600/25"
                : "bg-background/20 border-border/30 text-muted-foreground hover:bg-secondary/40"
            }`}
            size="sm"
            onClick={() => onGifsOnlyChange?.(!gifsOnly)}
          >
            <Video className="h-3 w-3 mr-2" />
            {tt("gifsOnly")}
            <span className="ml-auto text-[10px] uppercase tracking-wide">{gifsOnly ? "on" : "off"}</span>
          </Button>
        </CollapsibleContent>
      </Collapsible>

      <div className="h-px bg-border/30" />

      <Collapsible
        open={sectionsOpen.trendingTags}
        onOpenChange={(open) => setSectionsOpen((prev) => ({ ...prev, trendingTags: open }))}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full hover:text-foreground transition-colors py-2.5 px-3 rounded-lg bg-card border border-border shadow-sm hover:shadow-md">
          <span className="text-xs font-semibold text-foreground">{tt("trendingTags")}</span>
          <ChevronDown className="h-3.5 w-3.5 transition-transform data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-2">
          {trending.status === "loading" && trending.tags.length === 0 && (
            <div className="text-xs text-muted-foreground px-1 py-1">{tTrending("loading")}</div>
          )}
          {trending.tags.length > 0 && (
            <>
              <div className="px-1 text-[11px] text-muted-foreground">
                {tTrending("headingOn", { sources: trending.sources.map((id) => sources.find((source) => source.id === id)?.name ?? id).join(", ") })}
              </div>
              <TrendingTagBadges tags={trending.tags} onPick={(picked) => onTagAdd?.(picked.tag)} />
            </>
          )}
          {trending.status === "ready" && trending.tags.length === 0 && (
            <div className="text-xs text-muted-foreground px-1 py-1">{tTrending("empty")}</div>
          )}
          {trending.status === "error" && <div className="text-xs text-muted-foreground px-1 py-1">{tTrending("error")}</div>}
        </CollapsibleContent>
      </Collapsible>

      {/* Auth Dialog */}
      {authDialogSource && (
        <BooruAuthDialog
          open={authDialogOpen}
          onOpenChange={setAuthDialogOpen}
          source={authDialogSource}
          onSave={async () => {
            // Credentials saved, dialog will close
          }}
        />
      )}

      {reportingSource && (
        <ReportSourceDialog
          sourceId={reportingSource.id}
          sourceName={reportingSource.name}
          open={!!reportingSource}
          onOpenChange={(open) => !open && setReportingSource(null)}
        />
      )}
    </div>
  )
}
